import {
	MediaQueryKind,
	useWorkspaceMediaQuery,
	useIsTouchDevice
} from "client/Hooks";
import { SelectMode } from "client/Misc/commonTypes";
import { ensureNotNil, isNotNil } from "client/Misc/utils";
import { ExchangeEmailId } from "client/Api/Email/common";
import { EmailIdWithIsReadStatus } from "client_legacy/Email/emailEvents";
import {
	useCallback,
	useMemo
} from "react";
import {
	EmailAppAction,
	EmailAppActionContext,
	EmailAppActions
} from "../Actions/appActions";
import {
	ComposeEmailAction,
	ComposeEmailActionContext,
	ComposeEmailActionOptions,
	ComposeEmailActions
} from "../Actions/composeActions";
import {
	EmailSelectionAction,
	EmailSelectionActionContext,
	getSelectionActions
} from "../Actions/selectionActions";
import { MessageFlagState, MessageReadState } from "client/Api/Email/email";
import { EmailResponseKind } from "./EmailPage";
import { EmailComposerHandle } from "../EmailContent/EmailComposer/EmailComposer";
import { ActionAndOthersWithQuickActionIndex } from "client/Components";
import { ActionKind, ActionsWithContext } from "client_legacy/ndaw/actions";
import { never } from "client_legacy/ndaw/errors";

export type EmailActionBarOptions = {
	hasSession: boolean;
	showGoToOutlookAction: boolean;
	composeEmailActionOptions: ComposeEmailActionOptions;
	selectMode: SelectMode;
	selectionContext: EmailSelectionActionContext;
	isAttachmentUploadInProgress: boolean;
	composerHandle: EmailComposerHandle | null;
	onRespondToEmail(
		emailId: ExchangeEmailId,
		kind: EmailResponseKind,
		smtpAddress: string | null
	): Promise<void>;
	onChangeReadStatus(
		emailIds: readonly ExchangeEmailId[],
		readStatus: MessageReadState,
		smtpAddress: string | null
	): void;
	onChangeFlagStatus(
		emailIds: readonly ExchangeEmailId[],
		flagStatus: MessageFlagState,
		smtpAddress: string | null
	): void;
	onDeleteEmails(emails: readonly EmailIdWithIsReadStatus[], smtpAddress: string | null): void;
	onMoveEmails(emails: readonly EmailIdWithIsReadStatus[], smtpAddress: string | null): void;
	onComposeNewEmail(): void;
	onChangeSelectAll(selectAll: boolean): void;
	onSelectModeChange(selectMode: SelectMode): void;
	onShowUploadAttachmentInProgressError(): void;
};

export function useEmailActionBar(
	options: EmailActionBarOptions
): ActionsWithContext<CompositeActionContext> {
	const {
		hasSession,
		showGoToOutlookAction,
		composeEmailActionOptions,
		selectMode,
		selectionContext,
		isAttachmentUploadInProgress,
		composerHandle,
		onRespondToEmail,
		onChangeReadStatus,
		onChangeFlagStatus,
		onDeleteEmails,
		onMoveEmails,
		onComposeNewEmail,
		onChangeSelectAll,
		onSelectModeChange,
		onShowUploadAttachmentInProgressError
	} = options;

	const appActions = useMemo(
		(): readonly EmailAppAction[] => {
			const emailAppActions = new EmailAppActions({
				showGoToOutlookAction,
				handlers: {
					composeNewEmail: onComposeNewEmail,
					enableSelectMode: () => {
						onSelectModeChange(SelectMode.Manual);
					},
					disableSelectMode: () => {
						onSelectModeChange(SelectMode.Disabled);
					},
					changeSelectAll: onChangeSelectAll
				}
			});

			return emailAppActions.getAllActions();
		},
		[
			onChangeSelectAll,
			onComposeNewEmail,
			onSelectModeChange,
			showGoToOutlookAction
		]
	);

	const checkIfCanPerformAnActionOrShowError = useCallback(
		(): boolean => {
			if (!isAttachmentUploadInProgress) {
				return true;
			}

			onShowUploadAttachmentInProgressError();
			return false;
		},
		[isAttachmentUploadInProgress, onShowUploadAttachmentInProgressError]
	);

	const selectionActions = useMemo(
		(): readonly EmailSelectionAction[] => {
			return getSelectionActions(
				{
					move: onMoveEmails,
					changeFlagStatus: onChangeFlagStatus,
					changeReadStatus: onChangeReadStatus,
					delete: onDeleteEmails,
					respondToEmail: onRespondToEmail
				},
				checkIfCanPerformAnActionOrShowError,
				checkIfCanPerformAnActionOrShowError
			);
		},
		[
			checkIfCanPerformAnActionOrShowError,
			onChangeFlagStatus,
			onChangeReadStatus,
			onDeleteEmails,
			onMoveEmails,
			onRespondToEmail
		]
	);

	const composeActions = useMemo(
		(): readonly ComposeEmailAction[] => {
			return new ComposeEmailActions(
				composeEmailActionOptions,
				checkIfCanPerformAnActionOrShowError
			)
				.getAllActions();
		},
		[
			checkIfCanPerformAnActionOrShowError,
			composeEmailActionOptions
		]
	);

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const isTouch: boolean = useIsTouchDevice();

	const appContext = useMemo(
		(): EmailAppActionContext => ({
			selectedItems: selectionContext.selectedItems,
			isInSelectMode: selectMode === SelectMode.Manual || selectMode === SelectMode.FromLongTouch,
			isMobile,
			isTouch,
			hasSession
		}),
		[
			hasSession,
			isMobile,
			isTouch,
			selectMode,
			selectionContext.selectedItems
		]
	);

	const allActions = useMemo(
		(): readonly CompositeAction[] => {
			const result: CompositeAction[] = appActions.map(
				(x) => convertAction(x, (x) => x.app)
			);

			if (isNotNil(composerHandle)) {
				result.push(
					...composeActions.map(
						(x) => convertAction(x, (x) => ensureNotNil(x.compose))
					)
				);
			} else {
				result.push(
					...selectionActions.map(
						(x) => convertAction(x, (x) => x.selection)
					)
				);
			}

			return result;
		},
		[
			appActions,
			composeActions,
			composerHandle,
			selectionActions
		]
	);

	const actionContext = useMemo(
		(): CompositeActionContext => {
			const result: CompositeActionContext = {
				app: appContext,
				compose: null,
				selection: selectionContext
			};

			if (composerHandle) {
				result.compose = {
					composerHandle: composerHandle,
					isMobile
				};
			}

			return result;
		},
		[
			appContext,
			composerHandle,
			isMobile,
			selectionContext
		]
	);

	return {
		actions: allActions,
		context: actionContext
	};
}

export type CompositeActionContext = {
	app: EmailAppActionContext;
	compose: ComposeEmailActionContext | null;
	selection: EmailSelectionActionContext;
};

type CompositeAction = ActionAndOthersWithQuickActionIndex<CompositeActionContext>;

function convertAction<TOriginalActionContext>(
	action: ActionAndOthersWithQuickActionIndex<TOriginalActionContext>,
	contextSelector: (context: CompositeActionContext) => TOriginalActionContext
): CompositeAction {
	switch (action.kind) {
		case ActionKind.MultiSelectOption:
		case ActionKind.SelectOption:
			{
				const { title, isChecked } = action;
				return {
					...action,
					isChecked: typeof isChecked === "boolean" || typeof isChecked === "undefined"
						? isChecked
						: (ctx) => isChecked(contextSelector(ctx)),
					title: typeof title === "string"
						? title
						: (ctx) => title(contextSelector(ctx)),
					canExecute: (ctx) => action.canExecute(contextSelector(ctx)),
					execute: (ctx) => action.execute(contextSelector(ctx))
				};
			}
		case ActionKind.Regular:
			{
				const { title } = action;
				return {
					...action,
					title: typeof title === "string"
						? title
						: (ctx) => title(contextSelector(ctx)),
					canExecute: (ctx) => action.canExecute(contextSelector(ctx)),
					execute: (ctx) => action.execute(contextSelector(ctx))
				};
			}
		case ActionKind.BusyIndicator:
			{
				return {
					...action,
					canExecute: (ctx) => action.canExecute(contextSelector(ctx)),
					title: undefined,
					execute: undefined
				};
			}
		case ActionKind.Divider:
			{
				return {
					...action,
					canExecute: (ctx) => action.canExecute(contextSelector(ctx)),
					title: undefined,
					execute: undefined
				};
			}
		default:
			never(action);
	}
}
